import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  taxReportJ05, type CorrectionMode, type CorrectionSource, type TaxReport,
} from '@/api/taxReportJ05';
import { useToast } from '@/components/ToastProvider';
import { extractApiError } from '@/utils/extractApiError';
import TchTable from '@/shared/components/TchTable';

/**
 * ч.1295 — панель виправлень коригуючого розрахунку («звітний новий» / «уточнюючий»).
 *
 * За Порядком коригуючий містить ЛИШЕ виправлення: рядки поданого розрахунку
 * «на виключення» (ознака 1) і правильні «на введення» (ознака 0); у Д1 суми
 * ЄСВ за минулі періоди — рядками з кодом типу нарахувань 2 (донараховано) /
 * 3 (зменшено). Тут бухгалтер бере рядки з ПОДАНОГО розрахунку (зі знімка
 * пакета, що пішов на підпис) і додає їх у бланк нижче, де їх можна правити.
 */

const SECTION_LABEL: Record<string, string> = {
  D1: 'Д1 (ЄСВ)', '4DF': '4ДФ (ПДФО/ВЗ)', D5: 'Д5 (трудові відносини)', D6: 'Д6 (спецстаж)',
};
// Колонки для підбору (ключові графи; повний рядок видно у бланку після додавання).
const PICK_COLS: Record<string, { tag: string; label: string; right?: boolean }[]> = {
  D1: [
    { tag: 'T1RXXXXG7S', label: 'РНОКПП' }, { tag: 'T1RXXXXG111S', label: 'Прізвище' },
    { tag: 'T1RXXXXG112S', label: 'Імʼя' }, { tag: 'T1RXXXXG101', label: 'Міс.' },
    { tag: 'T1RXXXXG8', label: 'Кат.' }, { tag: 'T1RXXXXG9', label: 'КТН' },
    { tag: 'T1RXXXXG16', label: 'Дохід', right: true }, { tag: 'T1RXXXXG17', label: 'База', right: true },
    { tag: 'T1RXXXXG20', label: 'ЄСВ', right: true },
  ],
  '4DF': [
    { tag: 'T1RXXXXG02', label: 'РНОКПП' }, { tag: 'T1RXXXXG05', label: 'Озн.' },
    { tag: 'T1RXXXXG03A', label: 'Нараховано', right: true }, { tag: 'T1RXXXXG03', label: 'Виплачено', right: true },
    { tag: 'T1RXXXXG04A', label: 'ПДФО нарах.', right: true }, { tag: 'T1RXXXXG04', label: 'ПДФО перерах.', right: true },
    { tag: 'T1RXXXXG5A', label: 'ВЗ нарах.', right: true }, { tag: 'T1RXXXXG5', label: 'ВЗ перерах.', right: true },
  ],
  D5: [
    { tag: 'T1RXXXXG8S', label: 'РНОКПП' }, { tag: 'T1RXXXXG91S', label: 'Прізвище' },
    { tag: 'T1RXXXXG92S', label: 'Імʼя' }, { tag: 'T1RXXXXG7', label: 'Кат.' },
    { tag: 'T1RXXXXG101D', label: 'Початок' }, { tag: 'T1RXXXXG102D', label: 'Кінець' },
    { tag: 'T1RXXXXG15S', label: 'Посада' },
  ],
  D6: [
    { tag: 'T1RXXXXG6S', label: 'РНОКПП' }, { tag: 'T1RXXXXG81S', label: 'Прізвище' },
    { tag: 'T1RXXXXG82S', label: 'Імʼя' }, { tag: 'T1RXXXXG7S', label: 'Код підстави' },
    { tag: 'T1RXXXXG9', label: 'З дня' }, { tag: 'T1RXXXXG10', label: 'По день' },
  ],
};
const MODE_LABEL: Record<CorrectionMode, string> = {
  exclude: 'виключити (ознака 1)',
  replace: 'виправити (виключити + ввести)',
  include: 'ввести копією (ознака 0)',
  esv_add: 'донарахувати ЄСВ (КТН 2)',
  esv_reduce: 'зменшити ЄСВ (КТН 3)',
};
const SENT = new Set(['SENT', 'DELIVERED', 'ACCEPTED']);

function fmtDate8(v: string | null): string {
  if (!v || v.length !== 8) return v ?? '';
  return `${v.slice(0, 2)}.${v.slice(2, 4)}.${v.slice(4)}`;
}

export default function TaxReportJ05Corrections({ rep, onApplied }: { rep: TaxReport; onApplied: () => void }) {
  const toast = useToast();
  const sections = useMemo(
    () => (rep.appendices ?? []).filter((a) => a in SECTION_LABEL), [rep.appendices]);
  const [section, setSection] = useState(sections[0] ?? 'D1');
  const [sourceId, setSourceId] = useState(rep.corrects_report_id ?? '');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  // Подані розрахунки цієї організації за той самий період — джерело рядків.
  const candidatesQ = useQuery({
    queryKey: ['tax-reports-j05', 'sent-for-period', rep.organization_id, rep.period_year, rep.period_month, rep.period_quarter],
    queryFn: () => taxReportJ05.list({
      organization_id: rep.organization_id, period_year: rep.period_year,
      period_month: rep.period_month ?? undefined, limit: 100,
    }),
  });
  const candidates = (candidatesQ.data?.items ?? []).filter((r) =>
    r.id !== rep.id && SENT.has(r.submission_status ?? '')
    && (rep.period_quarter == null || r.period_quarter === rep.period_quarter));
  useEffect(() => {
    const only = candidates[0];
    if (!sourceId && candidates.length === 1 && only) setSourceId(only.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates.length]);

  const sourceQ = useQuery<CorrectionSource>({
    queryKey: ['tax-reports-j05', 'correction-source', rep.id, sourceId, section],
    queryFn: () => taxReportJ05.correctionSource(rep.id, { source_report_id: sourceId, section }),
    enabled: !!sourceId && !!section,
  });
  useEffect(() => { setPicked(new Set()); }, [sourceId, section]);

  const rows = sourceQ.data?.rows ?? [];
  const cols = PICK_COLS[section] ?? [];
  const allPicked = rows.length > 0 && picked.size === rows.length;

  async function apply(mode: CorrectionMode) {
    if (!sourceQ.data || picked.size === 0) return;
    setBusy(true);
    try {
      const sel = rows.filter((r) => picked.has(r.row_num));
      const res = await taxReportJ05.addCorrectionRows(rep.id, {
        section, rows: sel.map((r) => ({ mode, cells: r.cells })),
      });
      toast.show({
        type: 'success',
        message: `Додано рядків: ${res.added.length}. ${mode.startsWith('esv')
          ? 'Впишіть суму ЄСВ (гр.20) у бланку нижче.'
          : mode === 'replace' ? 'Виправте значення у рядку з ознакою 0 у бланку нижче.' : 'Перевірте їх у бланку нижче.'}`,
      });
      setPicked(new Set());
      onApplied();
    } catch (e) {
      toast.show({ type: 'error', message: extractApiError(e, 'Не вдалося додати рядки') });
    } finally {
      setBusy(false);
    }
  }

  async function addEmpty() {
    setBusy(true);
    try {
      await taxReportJ05.addCorrectionRows(rep.id, { section, rows: [{ mode: 'include', cells: {} }] });
      toast.show({ type: 'success', message: 'Додано порожній рядок на введення (ознака 0) — заповніть його у бланку нижче.' });
      onApplied();
    } catch (e) {
      toast.show({ type: 'error', message: extractApiError(e, 'Не вдалося додати рядок') });
    } finally {
      setBusy(false);
    }
  }

  const kind = rep.report_type === 'CORRECTING' ? 'Уточнюючий' : 'Звітний новий';
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem 0.9rem',
      marginBottom: '1rem', background: 'var(--bg-soft)' }}>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <b>{kind} розрахунок — лише виправлення</b>
        <span style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
          рядки 1–3 і чисельність не повторюються; у додатках — лише рядки з ознакою
          (1 — виключити поданий, 0 — ввести), у Д1 суми ЄСВ за минулі періоди — рядками з КТН 2/3;
          додаток без рядків у пакет не йде.
        </span>
      </div>

      <div className="doc-grid" style={{ marginTop: '0.6rem' }}>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Поданий розрахунок, що виправляється</label>
          <select value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
            <option value="">— оберіть поданий розрахунок за цей період —</option>
            {candidates.map((r) => (
              <option key={r.id} value={r.id}>
                № {r.report_ordinal} від {new Date(r.document_date).toLocaleDateString('uk-UA')}
                {r.report_type === 'REPORTING' ? '' : r.report_type === 'CORRECTING' ? ' · уточнюючий' : ' · звітний новий'}
                {r.has_sent_package ? '' : ' · без знімка пакета'}
              </option>
            ))}
            {rep.corrects_report_id && !candidates.some((c) => c.id === rep.corrects_report_id) && (
              <option value={rep.corrects_report_id}>обраний при створенні</option>
            )}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Додаток</label>
          <select value={section} onChange={(e) => setSection(e.target.value)}>
            {sections.map((s) => <option key={s} value={s}>{SECTION_LABEL[s]}</option>)}
          </select>
        </div>
      </div>

      {!sourceId && (
        <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          {candidatesQ.isLoading ? 'Шукаємо подані розрахунки…'
            : candidates.length === 0
              ? 'Поданих у ДПС розрахунків за цей період у системі немає — рядки на виключення впишіть у бланк нижче вручну (кнопка «+ рядок», у графі «Ознака» — 1), або додайте порожній рядок на введення.'
              : 'Оберіть поданий розрахунок — покажемо його рядки для підбору виправлень.'}
          {' '}
          <button type="button" onClick={addEmpty} disabled={busy} style={{ marginLeft: 6 }}>
            + порожній рядок на введення
          </button>
        </div>
      )}

      {sourceId && sourceQ.isLoading && <div style={{ color: 'var(--muted)' }}>Завантажуємо рядки поданого…</div>}
      {sourceId && sourceQ.isError && (
        <div style={{ color: 'var(--badge-red-fg)', fontSize: '0.85rem' }}>
          {extractApiError(sourceQ.error, 'Не вдалося прочитати поданий розрахунок')}
        </div>
      )}
      {sourceQ.data && (
        <>
          {!sourceQ.data.from_snapshot && (
            <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
              border: '1px solid var(--badge-amber-bd)', borderRadius: 6, padding: '0.4rem 0.6rem',
              fontSize: '0.82rem', margin: '0.5rem 0' }}>
              Знімка пакета, який пішов у ДПС, для цього розрахунку немає (подано до оновлення) —
              рядки переформовано з поточних даних. Рядок «на виключення» має повторювати ПОДАНИЙ:
              звірте суми з файлом у Кабінеті ДПС перед поданням.
            </div>
          )}
          <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
            <TchTable>
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input type="checkbox" checked={allPicked}
                      onChange={(e) => setPicked(e.target.checked ? new Set(rows.map((r) => r.row_num)) : new Set())} />
                  </th>
                  <th style={{ width: 34 }}>#</th>
                  {cols.map((c) => <th key={c.tag} style={c.right ? { textAlign: 'right' } : undefined}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={cols.length + 2} style={{ color: 'var(--muted)' }}>
                    У поданому розрахунку цей додаток порожній.
                  </td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.row_num}>
                    <td>
                      <input type="checkbox" checked={picked.has(r.row_num)}
                        onChange={(e) => setPicked((p) => {
                          const n = new Set(p); if (e.target.checked) n.add(r.row_num); else n.delete(r.row_num); return n;
                        })} />
                    </td>
                    <td>{r.row_num}</td>
                    {cols.map((c) => (
                      <td key={c.tag} style={c.right ? { textAlign: 'right' } : undefined}>
                        {c.tag.endsWith('D') ? fmtDate8(r.cells[c.tag] ?? null) : (r.cells[c.tag] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </TchTable>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.5rem', alignItems: 'center' }}>
            <span style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
              позначено {picked.size} — додати у коригуючий як:
            </span>
            {(['exclude', 'replace', 'include'] as CorrectionMode[]).map((m) => (
              <button key={m} type="button" disabled={busy || picked.size === 0} onClick={() => apply(m)}>
                {MODE_LABEL[m]}
              </button>
            ))}
            {section === 'D1' && (['esv_add', 'esv_reduce'] as CorrectionMode[]).map((m) => (
              <button key={m} type="button" disabled={busy || picked.size === 0} onClick={() => apply(m)}
                title="Рядок без ознаки з КТН 2/3: реквізити особи і місяць — з поданого; суму ЄСВ (гр.20) впишіть у бланку">
                {MODE_LABEL[m]}
              </button>
            ))}
            <button type="button" onClick={addEmpty} disabled={busy} style={{ marginLeft: 'auto' }}>
              + порожній рядок на введення
            </button>
          </div>
        </>
      )}
    </div>
  );
}
